import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";
import clsx from "clsx";

import {
  QueriesResponseModel,
  QueryListItemModel,
  QueryScopeType,
  QueryStatusType,
} from "@interfaces/index";
import { QUERY_WIDTHS } from "@utils/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useQueryGetQueries } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  PageContentContainer,
  RangePicker,
  DesktopPageTitle,
  TutorialButton,
  TableTabs,
  TableSubtabs,
  QueryItem,
} from "@components/index";

export const QueriesPage = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");

  const [activeTab, setActiveTab] = useState<QueryScopeType | "">("");
  const [activeSubtab, setActiveSubtab] = useState<QueryStatusType | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [tabs, setTabs] = useState<QueriesResponseModel["tabs"]>([]);
  const [businessCounts, setBusinessCounts] = useState<
    QueriesResponseModel["businessCounts"]
  >({});
  const [subtabs, setSubtabs] = useState<QueriesResponseModel["subtabs"]>([]);
  const [queries, setQueries] = useState<QueryListItemModel[]>([]);

  const availableTutorialNames = useMemo(() => {
    const arr = ["queriesViewTutorial"];
    if (queries.some(({ status }) => status.value === "awaiting_client")) {
      arr.push("queryReplyTutorial");
    }
    return arr;
  }, [queries]);

  const [openQueryId, setOpenQueryId] = useState("");
  const handleToggleQuery = (uniqueId: string) => {
    setOpenQueryId((prev) => (prev === uniqueId ? "" : uniqueId));
    setRedirectNeeds(true);
  };

  useEffect(() => {
    const params = queryString.parse(location.search);
    const scopeParam = (params?.activeTab as QueryScopeType) || "";
    setBusinessId(
      scopeParam === "tax_return"
        ? ""
        : (params?.businessId as string) ||
            defaultBusiness?.uniqueId ||
            businesses[0]?.uniqueId ||
            "",
    );
    setActiveTab(scopeParam);
    setActiveSubtab((params?.activeSubtab as QueryStatusType) || "");
    setStartDate((params?.startDate as string) || "");
    setEndDate((params?.endDate as string) || "");
    setOpenQueryId((params?.queryId as string) || "");
  }, [location, defaultBusiness, businesses]);

  useEffect(() => {
    if (!activeTab && tabs.length) {
      setActiveTab(tabs[0].value);
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    if (!activeSubtab && subtabs.length) {
      setActiveSubtab(subtabs[0].value);
    }
  }, [activeSubtab, subtabs]);

  const {
    isPending: isPendingGetQueries,
    isSuccess: isSuccessGetQueries,
    isStale: isStaleGetQueries,
    data: dataGetQueries,
    isError: isErrorGetQueries,
    error: errorGetQueries,
  } = useQueryGetQueries({
    businessId,
    startDate,
    endDate,
    scope: activeTab,
    status: activeSubtab,
  });

  useEffect(() => {
    if (isSuccessGetQueries && dataGetQueries) {
      setTabs(dataGetQueries.tabs);
      setBusinessCounts(dataGetQueries.businessCounts);
      setSubtabs(dataGetQueries.subtabs);
      setQueries(dataGetQueries.queries);
    }
  }, [isSuccessGetQueries, dataGetQueries]);

  useEffect(() => {
    if (isErrorGetQueries && errorGetQueries) {
      notify("error", errorGetQueries.message);
    }
  }, [isErrorGetQueries, errorGetQueries, notify]);

  useEffect(() => {
    setState({
      isLoading: isStaleGetQueries && isPendingGetQueries,
    });
  }, [isStaleGetQueries, isPendingGetQueries, setState]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        activeTab,
        activeSubtab,
        startDate,
        endDate,
        queryId: openQueryId,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    activeTab,
    activeSubtab,
    startDate,
    endDate,
    openQueryId,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setBusinessId(businessId);
  };

  const handleSelectTab = (value: QueryScopeType) => {
    setRedirectNeeds(true);
    setActiveTab(value);
    setOpenQueryId("");
    if (value === "tax_return") {
      setBusinessId("");
    } else if (!businessId) {
      setBusinessId(defaultBusiness?.uniqueId || businesses[0]?.uniqueId || "");
    }
  };

  const handleSelectSubtab = (value: QueryStatusType) => {
    setRedirectNeeds(true);
    setActiveSubtab(value);
    setOpenQueryId("");
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setEndDate(endDate);
  };

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Queries"
          tutorialNames={availableTutorialNames}
          faqCategory={["Queries"]}
        />
      )}
      <PageContentContainer noFilters>
        {isMobile && (
          <div className="pb-6">
            <TutorialButton
              variant="dark"
              tutorialNames={availableTutorialNames}
              faqCategory={["Queries"]}
            />
          </div>
        )}
        <TableTabs
          tabs={tabs}
          activeTab={activeTab}
          dataTestid="tabs_queriesPage"
          className="border-b border-lightGray"
          onClick={(value) => handleSelectTab(value as QueryScopeType)}
        />
        <div className="flex flex-col justify-between gap-y-4 pt-4 md:flex-row md:items-center">
          {activeTab !== "tax_return" && (
            <div
              className="flex flex-wrap gap-x-3 gap-y-2"
              data-testid="tabs_queriesPageBusiness"
            >
              {businesses.map(({ uniqueId, name }) => (
                <button
                  key={uniqueId}
                  type="button"
                  className={clsx(
                    "flex items-center gap-x-1.5 rounded-lg border px-4 py-2.5 text-200 font-bold transition-colors",
                    uniqueId === businessId
                      ? "border-primary-300 bg-primary-108 text-primary-300"
                      : "border-transparent bg-black-4 text-black-100 hover:bg-black-8",
                  )}
                  onClick={() => handleSelectBusiness(uniqueId)}
                >
                  {name}
                  {!businessCounts[uniqueId] ? null : (
                    <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-90 text-white-100">
                      {businessCounts[uniqueId]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-black-24 md:ml-auto">
            <RangePicker
              startDate={startDate}
              endDate={endDate}
              prefix="queriesPage"
              onSetStartDate={handleSetStartDate}
              onSetEndDate={handleSetEndDate}
              showPeriods={true}
              dataTestid="filter_dateRangeSelector"
            />
          </div>
        </div>
        <div className="flex flex-col gap-x-6 pt-6 md:flex-row">
          <div className="w-full shrink-0 md:max-w-[220px]">
            <TableSubtabs
              tabs={subtabs}
              activeTab={activeSubtab}
              dataTestid="subtabs_queriesPage"
              vertical={!isMobile}
              onClick={(value) => handleSelectSubtab(value as QueryStatusType)}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="flex max-h-[600px] flex-col gap-y-2 overflow-y-auto"
              data-testid="list_queries"
            >
              {!queries.length ? (
                <div className="pt-4 text-center text-black-54">No queries</div>
              ) : (
                queries.map((query) => (
                  <QueryItem
                    key={query.uniqueId}
                    query={query}
                    isOpen={openQueryId === query.uniqueId}
                    onToggle={() => handleToggleQuery(query.uniqueId)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </PageContentContainer>
    </div>
  );
};
